import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { StyledDetailsWrapper } from 'pages/TransactionsPage/ViewTransactionPage/styles';
import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { useTransactions } from 'pages/TransactionsPage/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { StyledButton } from 'pages/Firearms/ViewRegisteredFirearmPage/styles';
import { Accordion } from 'components/molecules/Accordion';
import Link from '@mui/material/Link';
import { colors } from 'theme/colors';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { EIconTypes } from 'constants/Icons';
import { CommentAccordion } from 'components/molecules/CommentAccordion';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useForm } from 'react-hook-form';
import { Loader } from 'components/atoms/Loader';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions } from 'models/table';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useLang } from 'models/langContext';
import { formatDate } from 'utils/date';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ECustomFieldObjectType } from 'models/customField';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { ETransactionItemType } from 'constants/TransactionItemType';
import {
  mapTransactionDetailsAncillaries,
  mapTransactionDetailsFirearms,
  transactionDetailsAncillariesTableColumns,
  transactionDetailsFirearmsTableColumns,
} from 'pages/TransactionsPage/helpers';
import { Table } from 'components/molecules/Table';
import { TransactionDetailsFirearmsCard } from 'pages/TransactionsPage/TransactionDetailsFirearmsCard';
import {
  EArtAncillaryTypeName,
  EArtAncillaryTypeNameUpperCase,
} from 'constants/ArtAccessoryType';
import { TransactionDetailsAncillariesCard } from 'pages/TransactionsPage/TransactionDetailsAncillariesCard';
import ComponentDetails from 'components/organisms/AncillaryDetails/ComponentDetails';
import AmmunitionDetails from 'components/organisms/AncillaryDetails/AmmunitionDetails';
import AccessoryDetails from 'components/organisms/AncillaryDetails/AccessoryDetails';
import {
  isAncillaryGroupTransactionDiscardable,
  isGroupTransactionDiscardable,
} from 'requests/transaction';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { TransactionDetailsMarkingsSection } from '../TransactionDetailsMarkingsSection';
import { getEnumByValue } from 'utils/enum';
import { useDictionary } from 'models/dictionaryContext';

const INACTIVE_FIREARM_ID = '11d094fc-0746-11ed-b939-0242ac120002';
const ILLEGAL_FIREARM_ID = 'ae5de668-67ac-4650-af26-23dd0cb97c89';

export enum ETransactionDetailsTranslationsKeys {
  noResults = 'users.search.noResults.info',
}

const shouldShowOnMap = (typeUniqueId: string) =>
  [
    ETransactionTypes.SEIZED,
    ETransactionTypes.RECOVERED,
    ETransactionTypes.STOLEN,
    ETransactionTypes.LOST,
    ETransactionTypes.SPOT_CHECK,
  ]
    .map((type) => type.valueOf())
    .includes(typeUniqueId);

export const getAncillaryViewRoute = (ancillaryType: string, uniqueId: string) => {
  switch (ancillaryType.toLowerCase()) {
    case EArtAncillaryTypeName.AMMUNITION.toLowerCase():
      return `${ERouteLinks.ViewAmmunition.replace(':id', uniqueId)}`;

    case EArtAncillaryTypeName.ACCESSORY.toLowerCase():
      return `${ERouteLinks.ViewAccessories.replace(':id', uniqueId)}`;

    case EArtAncillaryTypeName.COMPONENTS.toLowerCase():
    default:
      return `${ERouteLinks.ViewComponents.replace(':id', uniqueId)}`;
  }
};

const ViewTransactionPage = () => {
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
  const [currentPageComments, setCurrentPageComments] = useState<number>(1);
  const [perPageComments, setPerPageComments] = useState<EPerPages>(EPerPages.perPage25);
  const [currentPageFirearmsAncillaries, setCurrentPageFirearmsAncillaries] =
    useState<number>(1);
  const [perPageFirearmsAncillaries, setPerPageFirearmsAncillaries] = useState<EPerPages>(
    EPerPages.perPage25,
  );
  const [sortFirearmsAncillaries, setSortFirearmsAncillaries] = useState<ISortOptions | null>(
    null,
  );
  const { hasPermissions } = usePermission();
  const { getValidTranslationName } = useLang();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useThemeBreakpoint();
  const {
    handleGetTransactionDetailsData,
    transactionDetails,
    loadDictionariesForTransactionDetails,
    transactionType,
    make,
    model,
    firearmAction,
    firearmType,
    caliber,
    handleGetTransactionDetailsFirearmsData,
    transactionDetailsFirearmsContent,
    transactionDetailsFirearmsPaginator,
    handleGetTransactionDetailsAncillariesData,
    transactionDetailsAncillariesContent,
    transactionDetailsAncillariesPaginator,
    handleGetTransactionDetailsCommentsData,
    transactionDetailsCommentsContent,
    transactionDetailsCommentsPaginator,
    isTransactionDetailsCommentsDataLoading,
    checkIfTransactionIsLast,
    handleGetPreviousTransactionLocation,
    previousTransactionLocation,
  } = useTransactions();

  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();

  const { transactionAlert, setAlert, clearAlert } = useAlert();
  const [isFrtInformationVisible, setIsFrtInformationVisible] = useState<boolean>(false);
  const [isArtInformationVisible, setIsArtInformationVisible] = useState<boolean>(false);
  const [isLastTransaction, setIsLastTransaction] = useState<boolean | null>(null);
  const [showOnMapPlace, setShowOnMapPlace] = useState(0);
  const { midDateFormat, longDateFormat, shortDateFormat, getProperties } =
    useGlobalProperty();
  const { getCustomField, customFieldsOLE } = useLegalEntities();
  const [isTransactionDiscardable, setIsTransactionDiscardable] = useState(false);
  const [isFirearmAncillaryPaginationLoading, setIsFirearmAncillaryPaginationLoading] =
    useState(false);
  const [isCommentPaginationLoading, setIsCommentPaginationLoading] = useState(false);

  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isShowHidden: false,
    },
  });

  const isShowHidden: boolean = watch('isShowHidden');
  const licenceAuthorityValid =
    !transactionDetails || isAnyLicenceAuthorityValid(transactionDetails?.licenceAuthorityIds);

  useEffect(() => {
    if (transactionDetails) {
      if (transactionDetails.discardDisabled) {
        setIsTransactionDiscardable(false);
        return;
      }
      if (
        transactionDetails.transactionItemType === ETransactionItemType.FIREARM &&
        transactionDetails.firearmGroupUniqueId
      ) {
        isGroupTransactionDiscardable(id).then((res) => setIsTransactionDiscardable(res));
      } else if (
        transactionDetails.transactionItemType === ETransactionItemType.ANCILLARY &&
        transactionDetails.ancillaryGroupUniqueId
      ) {
        isAncillaryGroupTransactionDiscardable(id).then((res) =>
          setIsTransactionDiscardable(res),
        );
      } else {
        setIsTransactionDiscardable(true);
      }
    }
  }, [id, transactionDetails]);

  useEffect(() => {
    getProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      await getCustomField(ECustomFieldObjectType.FIREARM);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function loadDictionaryInit() {
      await loadDictionariesForTransactionDetails();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (transactionDetails) {
      checkIfTransactionIsLast(transactionDetails?.uniqueId).then((res) =>
        setIsLastTransaction(res.isLast),
      );

      (async function getPreviousTransactionLocation() {
        if (transactionDetails.typeUniqueId === ETransactionTypes.MOVED) {
          await handleGetPreviousTransactionLocation(id);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionDetails]);

  useEffect(() => {
    (async function getTransactionDetailsInit() {
      if (id) {
        await handleGetTransactionDetailsData(id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    (async function getTransactionDetailsFirearmsInit() {
      if (id && transactionDetails?.transactionItemType === ETransactionItemType.FIREARM) {
        setIsFirearmAncillaryPaginationLoading(true);
        await handleGetTransactionDetailsFirearmsData(
          id,
          currentPageFirearmsAncillaries,
          perPageFirearmsAncillaries,
          sortFirearmsAncillaries,
        );
        setIsFirearmAncillaryPaginationLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    sortFirearmsAncillaries,
    perPageFirearmsAncillaries,
    currentPageFirearmsAncillaries,
    transactionDetails,
  ]);

  useEffect(() => {
    (async function getTransactionDetailsAncillariesInit() {
      if (id && transactionDetails?.transactionItemType === ETransactionItemType.ANCILLARY) {
        setIsFirearmAncillaryPaginationLoading(true);
        await handleGetTransactionDetailsAncillariesData(
          id,
          currentPageFirearmsAncillaries,
          perPageFirearmsAncillaries,
          sortFirearmsAncillaries,
        );
        setIsFirearmAncillaryPaginationLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    sortFirearmsAncillaries,
    perPageFirearmsAncillaries,
    currentPageFirearmsAncillaries,
    transactionDetails,
  ]);

  useEffect(() => {
    (async function getTransactionDetailsCommentsDataInit() {
      if (id) {
        setIsCommentPaginationLoading(true);
        await handleGetTransactionDetailsCommentsData(
          id,
          currentPageComments,
          perPageComments,
          { withHidden: isShowHidden },
        );
        setIsCommentPaginationLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isShowHidden]);

  useEffect(
    () => () => clearAlert(ActionTypes.CLEAR_TRANSACTION_ALERT),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (transactionDetails) {
      if (shouldShowOnMap(transactionDetails.typeUniqueId)) {
        if (
          transactionDetails.latitude &&
          transactionDetails.longitude &&
          transactionDetails.initial &&
          !isLastTransaction
        ) {
          setShowOnMapPlace(1);
        } else if (
          transactionDetails.latitude &&
          transactionDetails.longitude &&
          transactionDetails.initial &&
          isLastTransaction
        ) {
          setShowOnMapPlace(1);
        } else if (transactionDetails.latitude && transactionDetails.longitude) {
          setShowOnMapPlace(2);
        }
      }
    }
  }, [transactionDetails, isLastTransaction]);

  useEffect(() => {
    const haveFirearmsSameFRT =
      !!transactionDetailsFirearmsContent.length &&
      transactionDetailsFirearmsContent.every(
        (f) => f.frtId === transactionDetailsFirearmsContent[0].frtId,
      );
    setIsFrtInformationVisible(haveFirearmsSameFRT);
  }, [transactionDetailsFirearmsContent]);

  useEffect(() => {
    const haveAncillariesSameART =
      !!transactionDetailsAncillariesContent.length &&
      transactionDetailsAncillariesContent.every(
        (f) => f.artUniqueId === transactionDetailsAncillariesContent[0].artUniqueId,
      );
    setIsArtInformationVisible(haveAncillariesSameART);
  }, [transactionDetailsAncillariesContent]);

  const renderShowFRTInformationButton = () => {
    if (isFrtInformationVisible && !transactionDetails?.firearmsWithDifferentMakesOrModels) {
      return (
        <Grid size={{ xs: 12, lg: 6 }}>
          <Link
            href={ERouteLinks.FRTDetails.replace(
              ':frtId',
              transactionDetailsFirearmsContent?.[0]?.frtUniqueId,
            )}
            underline="none"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              fullWidth
              label={t(
                'transactionDetails.firearmDetails.accordion.showFRTInformation.button',
              )}
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
            />
          </Link>
        </Grid>
      );
    }
    return null;
  };

  const renderShowARTInformationButton = () => {
    if (isArtInformationVisible && transactionDetails) {
      return (
        <Grid size={{ xs: 12, lg: 6 }}>
          <Link
            href={`${ERouteLinks.ViewAncillaries.replace(
              ':type',
              transactionDetailsAncillariesContent?.[0]?.ancillaryType.toLowerCase(),
            ).replace(':id', transactionDetailsAncillariesContent?.[0]?.artUniqueId)}`}
            underline="none"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              fullWidth
              label={t(
                'transactionDetails.ancillaryDetails.accordion.showARTInformation.button',
              )}
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
            />
          </Link>
        </Grid>
      );
    }
    return null;
  };

  const renderAncillaryDetails = () => {
    const ancillary = transactionDetailsAncillariesContent[0];
    switch (ancillary.ancillaryType as EArtAncillaryTypeNameUpperCase) {
      case EArtAncillaryTypeNameUpperCase.ACCESSORY: {
        return (
          <AccessoryDetails
            id={transactionDetailsAncillariesContent[0].uniqueId}
            isForTransactionDetails={true}
            initialTransaction={transactionDetails?.initial}
            transactionType={
              transactionDetails
                ? getEnumByValue(ETransactionTypes, transactionDetails.typeUniqueId)
                : undefined
            }
            marking={transactionDetailsAncillariesContent[0].marking}
          />
        );
      }
      case EArtAncillaryTypeNameUpperCase.AMMUNITION: {
        return (
          <AmmunitionDetails
            id={transactionDetailsAncillariesContent[0].uniqueId}
            isForTransactionDetails={true}
            initialTransaction={transactionDetails?.initial}
            transactionType={
              transactionDetails
                ? getEnumByValue(ETransactionTypes, transactionDetails.typeUniqueId)
                : undefined
            }
            marking={transactionDetailsAncillariesContent[0].marking}
          />
        );
      }
      case EArtAncillaryTypeNameUpperCase.COMPONENT: {
        return (
          <ComponentDetails
            id={transactionDetailsAncillariesContent[0].uniqueId}
            isForTransactionDetails={true}
            initialTransaction={transactionDetails?.initial}
            transactionType={
              transactionDetails
                ? getEnumByValue(ETransactionTypes, transactionDetails.typeUniqueId)
                : undefined
            }
            marking={transactionDetailsAncillariesContent[0].marking}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  let frtDetailsTitle = '';
  if (
    transactionDetails?.transactionItemType === ETransactionItemType.FIREARM &&
    transactionDetailsFirearmsContent.length &&
    !transactionDetails?.firearmsWithDifferentMakesOrModels
  ) {
    const { makeUniqueId, modelUniqueId, typeUniqueId, actionUniqueId, caliberUniqueId } =
      transactionDetailsFirearmsContent[0];
    const makeName = findInDictionary(makeUniqueId, make) ?? '-';
    const modelName = findInDictionary(modelUniqueId, model) ?? '-';
    const typeName = findInDictionary(typeUniqueId, firearmType) ?? '-';
    const actionName = findInDictionary(actionUniqueId, firearmAction) ?? '-';
    const caliberName = findInDictionary(caliberUniqueId, caliber) ?? '-';

    frtDetailsTitle = `${makeName}, ${modelName}, ${typeName}, ${actionName}, ${caliberName}`;
  } else if (
    transactionDetails?.transactionItemType === ETransactionItemType.ANCILLARY &&
    transactionDetailsAncillariesContent.length &&
    !transactionDetails?.firearmsWithDifferentMakesOrModels
  ) {
    const { ancillarySpecificType } = transactionDetailsAncillariesContent[0];
    frtDetailsTitle = ancillarySpecificType;
  }
  let showFrtDetails = true;
  if (
    (transactionDetails &&
      transactionDetails?.transactionItemType === ETransactionItemType.ANCILLARY &&
      transactionDetails?.ancillaryGroupUniqueId !== '') ||
    transactionDetailsFirearmsContent.length === 0
  ) {
    showFrtDetails = false;
  }

  let dataColor = EChipsVariants.primary;
  if (transactionDetails && transactionDetails.legalityUniqueId === ILLEGAL_FIREARM_ID) {
    dataColor = EChipsVariants.error;
  }
  if (transactionDetails && transactionDetails.activityUniqueId === INACTIVE_FIREARM_ID) {
    dataColor = EChipsVariants.inactive;
  }

  const navigateToFirearmsMap = () =>
    navigate(ERouteLinks.RegisteredFirearmsMap, {
      state: {
        transactionUniqueId: transactionDetails?.uniqueId,
        latitude: transactionDetails?.latitude,
        longitude: transactionDetails?.longitude,
        legalityUniqueId: transactionDetails?.legalityUniqueId,
        transactionFirearmsUniqueIds: transactionDetailsFirearmsContent.map(
          (firearm) => firearm.uniqueId,
        ),
        navigateBack: `${ERouteLinks.ViewTransaction.replace(':id', id as string)}`,
        showAdvanced: true,
        showLegalityBar: false,
        breadcrumbs: [
          {
            label: t('transactionDetails.transactionDetails.header'),
            route: ERouteLinks.Transactions,
          },
          {
            label: t('transactionDetails.transactionDetails.header'),
            route: `${ERouteLinks.ViewTransaction.replace(':id', id as string)}`,
          },
          t('registeredFirearms.firearmsLocation.showOnMap.label'),
        ],
      },
    });

  const navigateToAncillariesMap = () =>
    navigate(ERouteLinks.RegisteredAncillariesMap, {
      state: {
        transactionUniqueId: transactionDetails?.uniqueId,
        latitude: transactionDetails?.latitude,
        longitude: transactionDetails?.longitude,
        legalityUniqueId: transactionDetails?.legalityUniqueId,
        navigateBack: `${ERouteLinks.ViewTransaction.replace(':id', id as string)}`,
        showAdvanced: true,
        showLegalityBar: true,
        breadcrumbs: [
          {
            label: t('transactionDetails.transactionDetails.header'),
            route: ERouteLinks.Transactions,
          },
          {
            label: t('transactionDetails.transactionDetails.header'),
            route: `${ERouteLinks.ViewTransaction.replace(':id', id as string)}`,
          },
          t('ancillaries.ancillariesLocation.showOnMap.label'),
        ],
      },
    });

  const handleDelete = (transactionId: string) => {
    if (!transactionDetails?.anyElementInDisabledState) {
      navigate(ERouteLinks.CancelTransaction.replace(':id', transactionId));
    } else {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
        text: t('transactionDiscard.elementInDisabledState.message'),
        variant: EAlertVariants.error,
      });
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('transaction.header'),
            route: ERouteLinks.Transactions,
          },
          t('transactionDetails.transactionDetails.header'),
        ]}
      />

      <StyledPageTitle variant="h4">
        {t('transactionDetails.transactionDetails.header')}
      </StyledPageTitle>

      {transactionDetails !== null && isLastTransaction !== null ? (
        <Box sx={{ my: 3 }}>
          <StyledDetailsWrapper container sx={{ rowGap: 3 }} columns={10}>
            <Grid size={{ xs: 10, md: 5, lg: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('transactionDetails.transactionType.label')}
              </Typography>
              <Grid sx={{ maxWidth: { md: '90%', xs: '100%' } }}>
                <Chips
                  text={
                    parseDictionary(transactionType, 'name')?.[
                      transactionDetails?.typeUniqueId
                    ]
                  }
                  variant={dataColor}
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 10, md: 5, lg: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('transactionDetails.transactionDateAndTime.label')}
              </Typography>
              <Typography variant="body2">
                {formatDate(transactionDetails.date, longDateFormat)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 10, md: 5, lg: 2 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('transactionDetails.createdOn.label')}
              </Typography>
              <Typography variant="body2">
                {formatDate(transactionDetails.createdDate, longDateFormat)}
              </Typography>
            </Grid>
            {(showOnMapPlace === 0 || showOnMapPlace === 2) && (
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('transactionDetails.createdBy.label')}
                </Typography>
                <Typography variant="body2">{transactionDetails.createdBy}</Typography>
              </Grid>
            )}
            {hasPermissions([EPermission.DISCARD_TRANSACTION]) &&
              licenceAuthorityValid &&
              !transactionDetails.initial &&
              isLastTransaction &&
              isTransactionDiscardable && (
                <Grid
                  size={{ xs: 10, lg: 2 }}
                  sx={{
                    borderTop: {
                      xs: `1px solid ${colors.gray200}`,
                    },
                    pt: {
                      xs: 3,
                      lg: 0,
                    },
                  }}
                >
                  <Button
                    label={t('transactionDetails.discard.button')}
                    variant={EButtonVariants.outlined}
                    onClick={() => handleDelete(id)}
                    fullWidth
                    size={EButtonSizes.small}
                  />
                </Grid>
              )}
            {showOnMapPlace === 1 && (
              <>
                <Grid
                  size={{ xs: 10, lg: 4 }}
                  sx={{
                    pt: {
                      xs: 3,
                      lg: 0,
                    },
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    label={t('transactionDetails.showOnMap.button')}
                    variant={EButtonVariants.contained}
                    onClick={
                      transactionDetails.transactionItemType === ETransactionItemType.FIREARM
                        ? navigateToFirearmsMap
                        : navigateToAncillariesMap
                    }
                    fullWidth
                    size={EButtonSizes.small}
                  />
                </Grid>
                <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('transactionDetails.createdBy.label')}
                  </Typography>
                  <Typography variant="body2">{transactionDetails.createdBy}</Typography>
                </Grid>
              </>
            )}
            {transactionDetails.address &&
              transactionDetails.latitude &&
              transactionDetails.longitude && (
                <>
                  <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('transactionDetails.address.label')}
                    </Typography>
                    <Typography variant="body2">{transactionDetails.address}</Typography>
                  </Grid>
                  <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('transactionDetails.latitude.label')}
                    </Typography>
                    <Typography variant="body2">{transactionDetails.latitude}</Typography>
                  </Grid>
                  <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('transactionDetails.longitude.label')}
                    </Typography>
                    <Typography variant="body2">{transactionDetails.longitude}</Typography>
                  </Grid>
                  {previousTransactionLocation && (
                    <>
                      <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('transactionDetails.previousAddress.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(previousTransactionLocation.address)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('transactionDetails.previousLatitude.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(previousTransactionLocation.latitude)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('transactionDetails.previousLongitude.label')}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(previousTransactionLocation.longitude)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </>
              )}
            {showOnMapPlace === 2 && (
              <>
                {!licenceAuthorityValid && (
                  <Grid
                    size={{ md: 0, lg: 2 }}
                    sx={{
                      pt: {
                        xs: 3,
                        lg: 0,
                      },
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  />
                )}
                <Grid
                  size={{
                    xs: 10,
                    lg: !transactionDetails.initial && isLastTransaction ? 4 : 6,
                  }}
                  sx={{
                    pt: {
                      xs: 3,
                      lg: 0,
                    },
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    label={t('transactionDetails.showOnMap.button')}
                    variant={EButtonVariants.contained}
                    onClick={
                      transactionDetails.transactionItemType === ETransactionItemType.FIREARM
                        ? navigateToFirearmsMap
                        : navigateToAncillariesMap
                    }
                    size={EButtonSizes.small}
                    sx={{
                      minWidth: '100px',
                      width: !transactionDetails.initial && isLastTransaction ? '100%' : '67%',
                    }}
                  />
                </Grid>
              </>
            )}
            {transactionDetails.countryOfImport &&
              transactionDetails.countryOfImport.translations && (
                <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('transactionDetails.countryOfImport.label')}
                  </Typography>
                  <Typography variant="body2">
                    {getValidTranslationName(transactionDetails.countryOfImport.translations)}
                  </Typography>
                </Grid>
              )}
            {transactionDetails.importer && (
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('transactionDetails.importer.label')}
                </Typography>
                <Typography variant="body2">{transactionDetails.importer}</Typography>
              </Grid>
            )}
            {transactionDetails.countryOfExport &&
              transactionDetails.countryOfExport.translations && (
                <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('transactionDetails.countryOfExport.label')}
                  </Typography>
                  <Typography variant="body2">
                    {getValidTranslationName(transactionDetails.countryOfExport.translations)}
                  </Typography>
                </Grid>
              )}
            {transactionDetails.expirationDate && (
              <Grid size={{ xs: 10, md: 5, lg: 2 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {[ETransactionTypes.ASSIGNED_TEMPORARILY, ETransactionTypes.LEASED].includes(
                    transactionDetails.typeUniqueId as ETransactionTypes,
                  )
                    ? t('transactionDetails.expirationTime.label')
                    : t('transactionDetails.expirationDate.label')}
                </Typography>
                <Typography variant="body2">
                  {[ETransactionTypes.ASSIGNED_TEMPORARILY, ETransactionTypes.LEASED].includes(
                    transactionDetails.typeUniqueId as ETransactionTypes,
                  )
                    ? formatDate(transactionDetails?.expirationDate?.toString(), midDateFormat)
                    : formatDate(
                        transactionDetails?.expirationDate?.toString(),
                        shortDateFormat,
                      )}
                </Typography>
              </Grid>
            )}
            {transactionDetails?.typeUniqueId === ETransactionTypes.MODIFIED &&
              transactionDetails?.description && (
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('ancillaries.transaction.modification.label')}
                  </Typography>
                  <Typography variant="body2">
                    {getHtmlText(transactionDetails.description)}
                  </Typography>
                </Grid>
              )}
          </StyledDetailsWrapper>
          <Grid size={{ xs: 12 }}>
            {transactionAlert && (
              <Box sx={{ mt: 2 }}>
                <Alert text={transactionAlert.text} variant={transactionAlert.variant} />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', my: 2 }}>
            <StyledButton
              label={
                isAllExpanded
                  ? t('transactionDetails.collapseAll.link')
                  : t('transactionDetails.expandAll.link')
              }
              onClick={() => setIsAllExpanded((prev) => !prev)}
              variant={EButtonVariants.outlined}
              size={EButtonSizes.small}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('transactionDetails.ownerAndKeeper.accordion.name')}
              expanded={isAllExpanded}
            >
              <StyledDetailsWrapper container style={{ rowGap: 16, marginBottom: 5 }}>
                <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('transactionDetails.ownerAndKeeper.accordion.owner.label')}
                  </Typography>
                  <Typography variant="body2">
                    <StyledLegalLink
                      legalId={transactionDetails.ownerId}
                      legalType={getLegalEntityType(transactionDetails.ownerType)}
                      labelText={transactionDetails.owner}
                      legalVisibilityUniqueId={transactionDetails.ownerVisibilityUniqueId}
                    />
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('transactionDetails.ownerAndKeeper.accordion.keeper.label')}
                  </Typography>
                  <Typography variant="body2">
                    <StyledLegalLink
                      legalId={transactionDetails.keeperId}
                      legalType={getLegalEntityType(transactionDetails.keeperType)}
                      labelText={transactionDetails.keeper}
                      legalVisibilityUniqueId={transactionDetails.keeperVisibilityUniqueId}
                    />
                  </Typography>
                </Grid>
              </StyledDetailsWrapper>
            </Accordion>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={
                transactionDetails?.transactionItemType === ETransactionItemType.FIREARM
                  ? t('transactionDetails.firearmDetails.accordion.name')
                  : t('transactionDetails.ancillaryDetails.accordion.name')
              }
              name="firearmDetails"
              expanded={isAllExpanded}
            >
              {((transactionDetailsAncillariesContent &&
                transactionDetailsAncillariesContent.length === 0 &&
                transactionDetails?.transactionItemType === ETransactionItemType.ANCILLARY) ||
                (transactionDetailsFirearmsContent &&
                  transactionDetailsFirearmsContent.length === 0 &&
                  transactionDetails?.transactionItemType === ETransactionItemType.FIREARM)) &&
                !isFirearmAncillaryPaginationLoading && (
                  <Box sx={{ mt: 2 }}>
                    <Alert
                      text={t('transactionDetails.noAssetPresent.message')}
                      variant={EAlertVariants.warning}
                    />
                  </Box>
                )}
              <Grid container spacing={2} sx={{ px: 2, mb: 2 }}>
                {transactionDetails.firearmsWithDifferentMakesOrModels && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('transactionDetails.firearmDetails.accordion.firearmCount.label')}
                      </Typography>
                      <Typography variant="body2">
                        {transactionDetailsFirearmsContent.length}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {transactionDetails?.transactionItemType === ETransactionItemType.FIREARM &&
                  transactionDetailsFirearmsContent.length > 0 &&
                  transactionDetails.firearmGroupName && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('transactionDetails.firearmDetails.accordion.groupName.label')}
                        </Typography>
                        <Typography variant="body2">
                          {transactionDetails.firearmGroupName}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                {transactionDetails?.transactionItemType === ETransactionItemType.ANCILLARY &&
                  transactionDetailsAncillariesContent.length > 0 &&
                  transactionDetails.ancillaryGroupName && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('transactionDetails.firearmDetails.accordion.groupName.label')}
                        </Typography>
                        <Typography variant="body2">
                          {transactionDetails.ancillaryGroupName}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
              </Grid>
              {!transactionDetails.firearmsWithDifferentMakesOrModels && showFrtDetails && (
                <Grid
                  sx={{
                    border: `1px solid ${colors.primary25}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    mb: 2,
                  }}
                >
                  <Grid
                    container
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: colors.primary100,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Grid
                      size={{ xs: 12 }}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        rowGap: 1,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                      >
                        {frtDetailsTitle}
                      </Typography>
                      {transactionDetailsFirearmsContent.length === 1 &&
                        hasPermissions([EPermission.VIEW_FIREARMS]) && (
                          <Link
                            href={`${ERouteLinks.ViewRegisteredFirearm.replace(
                              ':id',
                              transactionDetailsFirearmsContent?.[0].uniqueId as string,
                            )}`}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                          >
                            <Button
                              label={t(
                                'registeredFirearms.firearmsDetails.transactions.accordion.details.link',
                              )}
                              variant={EButtonVariants.outlined}
                              size="small"
                              fullWidth={isMobile}
                            />
                          </Link>
                        )}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    columnSpacing={2}
                    rowSpacing={{ xs: 2, lg: 0 }}
                    sx={{ p: 2 }}
                  >
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {transactionDetails?.transactionItemType ===
                            ETransactionItemType.FIREARM &&
                            t(
                              'transactionDetails.firearmDetails.accordion.firearmsReferenceNo.label',
                            )}
                        </Typography>
                        <Typography variant="body2">
                          {transactionDetails?.transactionItemType ===
                            ETransactionItemType.FIREARM &&
                            transactionDetailsFirearmsContent?.[0]?.frtId}
                        </Typography>
                      </Stack>
                    </Grid>
                    {renderShowFRTInformationButton()}
                    {transactionDetailsFirearmsContent.length === 1 && (
                      <Grid
                        container
                        size={{ xs: 12 }}
                        columnSpacing={2}
                        rowSpacing={{ xs: 2, lg: 0 }}
                        sx={{ borderTop: `1px solid ${colors.primary25}`, mt: 2, pt: 3 }}
                      >
                        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(
                              'transactionDetails.firearmDetails.accordion.firecycleID.label',
                            )}
                          </Typography>
                          <Typography variant="body2">
                            {transactionDetailsFirearmsContent[0]?.fireCycleId || '-'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(
                              'transactionDetails.firearmDetails.accordion.serialNumber.label',
                            )}
                          </Typography>
                          <Typography variant="body2">
                            {transactionDetailsFirearmsContent[0]?.serialNumber || '-'}
                          </Typography>
                        </Grid>
                        {transactionDetails?.initial && (
                          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                              {getEnumByValue(
                                ETransactionTypes,
                                transactionDetails.typeUniqueId,
                              ) === ETransactionTypes.DECLARED_IMPORTED
                                ? t(
                                    'transactionDetails.firearmDetails.accordion.importMarking.column',
                                  )
                                : t(
                                    'transactionDetails.firearmDetails.accordion.appliedMarking.column',
                                  )}
                            </Typography>
                            <Typography variant="body2">
                              {emptyFieldHolder(transactionDetailsFirearmsContent[0]?.marking)}
                            </Typography>
                          </Grid>
                        )}
                        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(
                              'transactionDetails.firearmDetails.accordion.manufacturedDate.label',
                            )}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(
                              transactionDetailsFirearmsContent[0]?.manufacturedAt,
                              shortDateFormat,
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                    {renderShowARTInformationButton()}
                  </Grid>
                </Grid>
              )}

              {transactionDetailsAncillariesContent &&
                transactionDetailsAncillariesContent.length === 1 &&
                renderAncillaryDetails()}
              {transactionDetails?.transactionItemType === ETransactionItemType.FIREARM &&
                transactionDetailsFirearmsContent.length > 1 && (
                  <Grid>
                    {isDesktop ? (
                      <Table
                        columns={transactionDetailsFirearmsTableColumns(
                          {
                            isDifferentMakesOrModels:
                              transactionDetails.firearmsWithDifferentMakesOrModels,
                            handleView: (uniqueId: string) =>
                              hasPermissions([EPermission.VIEW_FIREARMS])
                                ? navigate(
                                    `${ERouteLinks.ViewRegisteredFirearm.replace(
                                      ':id',
                                      uniqueId,
                                    )}`,
                                  )
                                : null,
                            customFields: customFieldsOLE,
                            initial: transactionDetails.initial,
                            t: t,
                            transactionType: getEnumByValue(
                              ETransactionTypes,
                              transactionDetails.typeUniqueId,
                            ),
                          },
                          selectedLanguage?.uniqueId,
                          shortDateFormat,
                        )}
                        dataSource={mapTransactionDetailsFirearms(
                          transactionDetailsFirearmsContent,
                          shortDateFormat,
                          make,
                          model,
                          firearmAction,
                          firearmType,
                          caliber,
                        )}
                        onSort={setSortFirearmsAncillaries}
                        translationsKeys={ETransactionDetailsTranslationsKeys}
                      />
                    ) : (
                      <CardList
                        items={mapTransactionDetailsFirearms(
                          transactionDetailsFirearmsContent,
                          shortDateFormat,
                          make,
                          model,
                          firearmAction,
                          firearmType,
                          caliber,
                        )}
                        translationsKeys={ETransactionDetailsTranslationsKeys}
                        render={(data, index) => (
                          <TransactionDetailsFirearmsCard
                            data={data}
                            key={index}
                            initial={transactionDetails?.initial}
                            isDifferentMakesOrModels={
                              transactionDetails.firearmsWithDifferentMakesOrModels
                            }
                            transactionType={getEnumByValue(
                              ETransactionTypes,
                              transactionDetails.typeUniqueId,
                            )}
                            handleView={(uniqueId: string) =>
                              hasPermissions([EPermission.VIEW_FIREARMS])
                                ? navigate(
                                    ERouteLinks.ViewTransaction.replace(':id', uniqueId),
                                  )
                                : null
                            }
                          />
                        )}
                      />
                    )}

                    {transactionDetailsFirearmsPaginator && (
                      <Pagination
                        count={transactionDetailsFirearmsPaginator.totalElements}
                        onChangePage={(page) => setCurrentPageFirearmsAncillaries(page)}
                        current={currentPageFirearmsAncillaries}
                        perPage={perPageFirearmsAncillaries}
                        onChangePerPage={(value) => setPerPageFirearmsAncillaries(value)}
                        isLoading={isFirearmAncillaryPaginationLoading}
                      />
                    )}
                  </Grid>
                )}
              {transactionDetails?.transactionItemType === ETransactionItemType.ANCILLARY &&
                transactionDetailsAncillariesContent?.length > 1 && (
                  <Grid>
                    {isDesktop ? (
                      <Table
                        columns={transactionDetailsAncillariesTableColumns({
                          showDetail: hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES]),
                          initial: transactionDetails.initial,
                          transactionType: getEnumByValue(
                            ETransactionTypes,
                            transactionDetails.typeUniqueId,
                          ),
                        })}
                        dataSource={mapTransactionDetailsAncillaries(
                          transactionDetailsAncillariesContent,
                          shortDateFormat,
                        )}
                        onSort={setSortFirearmsAncillaries}
                        translationsKeys={ETransactionDetailsTranslationsKeys}
                      />
                    ) : (
                      <CardList
                        items={mapTransactionDetailsAncillaries(
                          transactionDetailsAncillariesContent,
                          shortDateFormat,
                        )}
                        translationsKeys={ETransactionDetailsTranslationsKeys}
                        render={(data, index) => (
                          <TransactionDetailsAncillariesCard
                            data={data}
                            key={index}
                            transactionType={getEnumByValue(
                              ETransactionTypes,
                              transactionDetails.typeUniqueId,
                            )}
                            showDetail={hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES])}
                            initial={transactionDetails.initial}
                          />
                        )}
                      />
                    )}
                    {transactionDetailsAncillariesPaginator && (
                      <Pagination
                        count={transactionDetailsAncillariesPaginator.totalElements}
                        onChangePage={(page) => setCurrentPageFirearmsAncillaries(page)}
                        current={currentPageFirearmsAncillaries}
                        perPage={perPageFirearmsAncillaries}
                        onChangePerPage={(value) => setPerPageFirearmsAncillaries(value)}
                        isLoading={isFirearmAncillaryPaginationLoading}
                      />
                    )}
                  </Grid>
                )}
            </Accordion>
          </Grid>
          {!transactionDetails?.initial && (
            <Grid size={{ xs: 12 }}>
              <TransactionDetailsMarkingsSection
                id={id}
                isAllExpanded={isAllExpanded}
                ancillaryDictionaryValue={
                  !!transactionDetails &&
                  transactionDetails.transactionItemType === ETransactionItemType.ANCILLARY
                    ? transactionDetailsAncillariesContent[0]?.ancillaryDictionaryValue
                    : undefined
                }
                transactionType={
                  transactionDetails
                    ? getEnumByValue(ETransactionTypes, transactionDetails.typeUniqueId)
                    : undefined
                }
              />
            </Grid>
          )}

          {hasPermissions(EPermission.MANAGE_TRANSACTION_COMMENTS) && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                title={t('transactionDetails.comments.accordion.name')}
                expanded={isAllExpanded}
                name="comments"
              >
                <Grid
                  sx={{
                    marginBottom: { xs: '20px', sm: '16px' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 5,
                  }}
                >
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormCheckbox
                      name="isShowHidden"
                      label={t(
                        'transactionDetails.comments.accordion.showHiddenComments.label',
                      )}
                      control={control}
                      errors={errors}
                      withValidation={false}
                    />
                  </Grid>
                  {licenceAuthorityValid && (
                    <Grid
                      size={{ xs: 12, sm: 6 }}
                      sx={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <Button
                        label={t('transactionDetails.comments.accordion.addComment.button')}
                        variant={EButtonVariants.contained}
                        icon={EIconTypes.plus}
                        onClick={() => {
                          navigate(
                            `${ERouteLinks.TransactionAddComment.replace(
                              ':id',
                              id as string,
                            )}?expand=comments`,
                          );
                        }}
                        fullWidth={isMobile}
                      />
                    </Grid>
                  )}
                </Grid>
                {!isTransactionDetailsCommentsDataLoading ? (
                  transactionDetailsCommentsContent?.map((item) => (
                    <Box key={item.uniqueId} sx={{ marginBottom: '8px' }}>
                      <CommentAccordion
                        name="comments"
                        comment={item}
                        deleteLink={
                          licenceAuthorityValid
                            ? ERouteLinks.TransactionDeleteComment.replace(':id', id).replace(
                                ':commentId',
                                item.uniqueId,
                              )
                            : undefined
                        }
                      />
                    </Box>
                  ))
                ) : (
                  <Loader isLoading={isTransactionDetailsCommentsDataLoading} />
                )}
                {!transactionDetailsCommentsContent?.length && (
                  <Typography
                    variant="subtitle2"
                    sx={{ color: colors.primary100, mb: 2, textAlign: 'center' }}
                  >
                    {t('legalEntities.individual.accordion.legalEntityTable.noResults.text')}
                  </Typography>
                )}
                {transactionDetailsCommentsPaginator && (
                  <Pagination
                    count={transactionDetailsCommentsPaginator?.totalElements}
                    perPage={perPageComments}
                    onChangePage={(page) => {
                      setCurrentPageComments(page);
                    }}
                    onChangePerPage={(value) => {
                      setPerPageComments(value);
                    }}
                    current={currentPageComments}
                    isLoading={isCommentPaginationLoading}
                  />
                )}
              </Accordion>
            </Grid>
          )}
        </Box>
      ) : (
        <Loader isLoading />
      )}
    </>
  );
};

export default ViewTransactionPage;
